import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../hooks/useAuth';
import { SettingsProvider } from '../hooks/useSettings';
import { ToastProvider } from '../components/ui/Toast';
import MainLayout from '../components/layout/MainLayout';
import Dashboard from '../pages/Dashboard';
import Login from '../pages/Login';
import Admins from '../pages/Admins';
import ProfileSettings from '../pages/ProfileSettings';
import Customers from '../pages/Customers';
import Drivers from '../pages/Drivers';
import Orders from '../pages/Orders';
import BlockedNumbers from '../pages/BlockedNumbers';
import Agents from '../pages/Agents';
import Transactions from '../pages/Transactions';
import RatingsPage from '../pages/RatingsPage';
import DeleteRequests from '../pages/DeleteRequests';
import Map from '../pages/MapTracking';
import Reports from '../pages/Reports';
import Notifications from '../pages/Notifications';
import BroadcastHistory from '../pages/BroadcastHistory';
import Banners from '../pages/Banners';
import SecurityLogs from '../pages/SecurityLogs';
import Settings from '../pages/Settings';
import AppVersions from '../pages/AppVersions';
import AdminActivity from '../pages/AdminActivity';

import './index.css';

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
/**
 * Picks the right landing route based on the logged-in user's role.
 * Operators don't have the Dashboard, so they go straight to Orders.
 */
function LandingRedirect() {
  const { user } = useAuth();
  const target = user?.type === 'operator' ? '/orders' : '/dashboard';
  return <Navigate to={target} replace />;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, initialized } = useAuth();

  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50/30 to-cyan-50/20">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-500 rounded-3xl mx-auto mb-4 animate-pulse shadow-soft-xl"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * App Component
 * Root component with routing and context providers
 */

function AppContent() {
  const { isAuthenticated, initialized } = useAuth();

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50/30 to-cyan-50/20">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-500 rounded-3xl mx-auto mb-4 animate-pulse shadow-soft-xl"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Route */}
      <Route
        path="/login"
        element={isAuthenticated ? <LandingRedirect /> : <Login />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LandingRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="admins" element={<Admins />} />
        <Route path="customers" element={<Customers />} />
        <Route path="drivers" element={<Drivers />} />
        <Route path="orders" element={<Orders />} />
        <Route path="blocked-numbers" element={<BlockedNumbers />} />
        <Route path="agents" element={<Agents />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="ratings" element={<RatingsPage />} />
        <Route path="profile" element={<ProfileSettings />} />
        <Route path="delete-requests" element={<DeleteRequests />} />
        <Route path="map" element={<Map />} />
        <Route path="reports" element={<Reports />} /> 
        <Route path="notifications" element={<Notifications />} />
        <Route path="broadcasts" element={<BroadcastHistory />} />
        <Route path="banners" element={<Banners />} />
        <Route path="security-logs" element={<SecurityLogs />} />
        <Route path="settings" element={<Settings />} />
        <Route path="app-versions" element={<AppVersions />} />
        <Route path="admin-activity" element={<AdminActivity />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <ToastProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </ToastProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <h1 className="text-6xl font-bold text-gray-300">404</h1>
      <p className="text-xl text-gray-600 mt-4">Page not found</p>
      <a href="/dashboard" className="inline-flex items-center gap-2 px-4 py-2 mt-6 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
        Go to Dashboard
      </a>
    </div>
  );
}

export default App;